/**
 * phoneCodeHelpers.js
 *
 * Utilities for extracting country and city information from phone numbers.
 * Focused on EU, India, and key global country codes, with landline city
 * lookup for Netherlands (+31) and India (+91) — the two Architecture BRIO offices.
 */

// ---------------------------------------------------------------------------
// Country code lookup — sorted longest code first so e.g. +353 matches before +3
// ---------------------------------------------------------------------------
const COUNTRY_CODES = [
  { code: '971', country: 'UAE' },
  { code: '966', country: 'Saudi Arabia' },
  { code: '852', country: 'Hong Kong' },
  { code: '420', country: 'Czech Republic' },
  { code: '386', country: 'Slovenia' },
  { code: '385', country: 'Croatia' },
  { code: '381', country: 'Serbia' },
  { code: '380', country: 'Ukraine' },
  { code: '372', country: 'Estonia' },
  { code: '371', country: 'Latvia' },
  { code: '370', country: 'Lithuania' },
  { code: '359', country: 'Bulgaria' },
  { code: '358', country: 'Finland' },
  { code: '353', country: 'Ireland' },
  { code: '351', country: 'Portugal' },
  { code: '254', country: 'Kenya' },
  { code: '234', country: 'Nigeria' },
  { code: '91', country: 'India' },
  { code: '90', country: 'Turkey' },
  { code: '86', country: 'China' },
  { code: '82', country: 'South Korea' },
  { code: '81', country: 'Japan' },
  { code: '65', country: 'Singapore' },
  { code: '61', country: 'Australia' },
  { code: '55', country: 'Brazil' },
  { code: '52', country: 'Mexico' },
  { code: '49', country: 'Germany' },
  { code: '48', country: 'Poland' },
  { code: '47', country: 'Norway' },
  { code: '46', country: 'Sweden' },
  { code: '45', country: 'Denmark' },
  { code: '44', country: 'United Kingdom' },
  { code: '43', country: 'Austria' },
  { code: '41', country: 'Switzerland' },
  { code: '40', country: 'Romania' },
  { code: '39', country: 'Italy' },
  { code: '36', country: 'Hungary' },
  { code: '34', country: 'Spain' },
  { code: '33', country: 'France' },
  { code: '32', country: 'Belgium' },
  { code: '31', country: 'Netherlands' },
  { code: '30', country: 'Greece' },
  { code: '27', country: 'South Africa' },
  { code: '7', country: 'Russia' },
  { code: '1', country: 'United States' },
];

// ---------------------------------------------------------------------------
// Netherlands landline area codes (digits after +31)
// Sorted longest first (all are 2-digit here, but kept consistent)
// ---------------------------------------------------------------------------
const NL_AREA_CODES = [
  { code: '79', city: 'Zoetermeer' },
  { code: '78', city: 'Dordrecht' },
  { code: '71', city: 'Leiden' },
  { code: '70', city: 'The Hague' },
  { code: '58', city: 'Leeuwarden' },
  { code: '55', city: 'Apeldoorn' },
  { code: '53', city: 'Enschede' },
  { code: '50', city: 'Groningen' },
  { code: '43', city: 'Maastricht' },
  { code: '40', city: 'Eindhoven' },
  { code: '38', city: 'Zwolle' },
  { code: '36', city: 'Almere' },
  { code: '35', city: 'Hilversum' },
  { code: '33', city: 'Amersfoort' },
  { code: '30', city: 'Utrecht' },
  { code: '26', city: 'Arnhem' },
  { code: '24', city: 'Nijmegen' },
  { code: '23', city: 'Haarlem' },
  { code: '20', city: 'Amsterdam' },
  { code: '15', city: 'Delft' },
  { code: '14', city: 'Breda' },
  { code: '13', city: 'Tilburg' },
  { code: '10', city: 'Rotterdam' },
];

// ---------------------------------------------------------------------------
// India landline area codes (digits after +91)
// Sorted longest first so 3-digit codes match before 2-digit codes
// ---------------------------------------------------------------------------
const IN_AREA_CODES = [
  { code: '821', city: 'Mysore' },
  { code: '755', city: 'Bhopal' },
  { code: '731', city: 'Indore' },
  { code: '674', city: 'Bhubaneswar' },
  { code: '657', city: 'Jamshedpur' },
  { code: '651', city: 'Ranchi' },
  { code: '612', city: 'Patna' },
  { code: '512', city: 'Lucknow' },
  { code: '484', city: 'Kochi' },
  { code: '471', city: 'Thiruvananthapuram' },
  { code: '413', city: 'Kolhapur' },
  { code: '361', city: 'Guwahati' },
  { code: '265', city: 'Vadodara' },
  { code: '261', city: 'Surat' },
  { code: '172', city: 'Chandigarh' },
  { code: '141', city: 'Jaipur' },
  { code: '80', city: 'Bangalore' },
  { code: '79', city: 'Ahmedabad' },
  { code: '44', city: 'Chennai' },
  { code: '40', city: 'Hyderabad' },
  { code: '33', city: 'Kolkata' },
  { code: '22', city: 'Mumbai' },
  { code: '20', city: 'Pune' },
  { code: '11', city: 'Delhi/New Delhi' },
];

/**
 * Strip all non-digit characters from a phone number, preserving a leading +.
 * Returns the digits-only string (without the +).
 */
function stripPhone(phoneNumber) {
  if (typeof phoneNumber !== 'string') return '';
  // Remove everything except digits and leading +
  const cleaned = phoneNumber.replace(/[\s\-()]/g, '');
  // Drop the leading + if present, we just need the digits
  return cleaned.startsWith('+') ? cleaned.slice(1) : cleaned;
}

/**
 * Match a country code from the digit string.
 * Returns { code, country, remaining } or null.
 */
function matchCountryCode(digits) {
  for (const entry of COUNTRY_CODES) {
    if (digits.startsWith(entry.code)) {
      return {
        code: entry.code,
        country: entry.country,
        remaining: digits.slice(entry.code.length),
      };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * getCountryFromPhone(phoneNumber)
 *
 * Given a phone number string (may contain +, spaces, dashes, parens),
 * extract the international country code and return the country name.
 * Returns null if the country code is not recognized.
 *
 * @param {string} phoneNumber - e.g. "+31 20 123 4567" or "0091-22-12345678"
 * @returns {string|null} Country name, or null if unrecognized.
 */
export function getCountryFromPhone(phoneNumber) {
  const digits = stripPhone(phoneNumber);
  if (!digits) return null;

  const match = matchCountryCode(digits);
  return match ? match.country : null;
}

/**
 * getCityFromLandline(phoneNumber)
 *
 * Given a phone number, attempt to identify the city from its landline area
 * code. Only Netherlands (+31) and India (+91) are supported.
 *
 * Returns null if:
 *  - The country code is not NL or IN
 *  - The number appears to be a mobile number
 *  - The area code is not recognized
 *
 * @param {string} phoneNumber - e.g. "+31 20 123 4567" or "+91 22 2345 6789"
 * @returns {{ city: string, country: string } | null}
 */
export function getCityFromLandline(phoneNumber) {
  const digits = stripPhone(phoneNumber);
  if (!digits) return null;

  const match = matchCountryCode(digits);
  if (!match) return null;

  const { country, remaining } = match;

  // --- Netherlands ---
  if (country === 'Netherlands') {
    // Mobile numbers start with 6 after the country code — skip
    if (remaining.startsWith('6')) return null;

    for (const area of NL_AREA_CODES) {
      if (remaining.startsWith(area.code)) {
        return { city: area.city, country: 'Netherlands' };
      }
    }
    return null;
  }

  // --- India ---
  if (country === 'India') {
    // Indian mobile numbers start with 6, 7, 8, or 9 and are 10 digits long.
    // If the subscriber digits (after country code) are 10 digits and start
    // with one of those prefixes, treat it as mobile.
    if (
      remaining.length === 10 &&
      ['6', '7', '8', '9'].includes(remaining[0])
    ) {
      return null;
    }

    for (const area of IN_AREA_CODES) {
      if (remaining.startsWith(area.code)) {
        return { city: area.city, country: 'India' };
      }
    }
    return null;
  }

  // Other countries — no city lookup available
  return null;
}
